import { useEffect } from 'react';
import {
  deepBlockIdFor,
  isAllTopLevelSelected,
  isEntireFieldSelected,
  editableFieldForSelectAll,
} from '../../../utils/blockSelectionUtils';

/**
 * Progressive Ctrl/Cmd+A (Notion-style):
 * 1) native select-all inside the focused field
 * 2) select that block
 * 3) select every top-level block in the doc
 */
export default function useSelectAll({
  blocksRef,
  editorRootRef,
  selectedBlockIdRef,
  multiSelectedRef,
  selectBlockOnly,
  selectAllTopLevel,
}) {
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'a') return;
      if (e.altKey || e.shiftKey) return;

      const root = editorRootRef.current;
      if (!root) return;

      const active = document.activeElement;
      // Leave picker/search inputs alone
      if (active?.tagName === 'INPUT' && root.contains(active)) return;

      const hasBlockSel =
        !!selectedBlockIdRef.current || multiSelectedRef.current.length > 0;
      const field = editableFieldForSelectAll(root, active);
      const focusInside = active && root.contains(active);
      // Allow selection-based targeting when focus has drifted to <body>
      if (!focusInside && !hasBlockSel && !field) return;

      const tree = blocksRef.current || [];
      if (isAllTopLevelSelected(tree, multiSelectedRef.current)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      if (hasBlockSel) {
        e.preventDefault();
        e.stopPropagation();
        selectAllTopLevel();
        return;
      }

      if (!field) return;

      if (!isEntireFieldSelected(field)) {
        return;
      }

      const blockId = deepBlockIdFor(field);
      if (!blockId) return;
      e.preventDefault();
      e.stopPropagation();
      selectBlockOnly(blockId);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [blocksRef, editorRootRef, selectAllTopLevel, selectBlockOnly, selectedBlockIdRef, multiSelectedRef]);
}
